import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink} from 'react-router';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Link,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faAngleLeft,
    faAngleRight,
    faArrowsRotate,
    faCalendarDay,
    faChevronDown,
    faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import dayjs, {Dayjs} from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {CalendarAccessDto, CalendarEntryDto, CalendarViewDto, EUserGroup} from 'apiClient/Api';

/** Umfang des angezeigten Zeitfensters. */
type RangeMode = 'week' | 'month';

/**
 * Der eigene Labelwin-Kalender.
 *
 * Die Person ist nie Teil der Anfrage — der Server leitet sie aus dem LabelBenutzer des
 * angemeldeten Kontos ab. Diese Seite kann deshalb keinen fremden Kalender anzeigen, und es gibt
 * hier bewusst keine Personenauswahl.
 *
 * Vor dem ersten Abruf wird `GET /Calendar/Me/Access` gefragt. Der antwortet immer mit 200 und
 * sagt in Klartext, ob und warum der Kalender nicht zur Verfügung steht ("kein LabelBenutzer
 * hinterlegt" ist der häufigste Fall und ein Zustand, kein Fehler) — so muss die Seite nicht
 * erst in ein 403 laufen, um das herauszufinden.
 */
export const MyCalendar: React.FC = () => {
    const {t, i18n} = useTranslation();
    useDocumentTitle('calendar.title');
    const context = useApplicationContext();

    const [access, setAccess] = useState<CalendarAccessDto | undefined>(undefined);
    const [accessLoading, setAccessLoading] = useState(true);
    const [view, setView] = useState<CalendarViewDto | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const [anchor, setAnchor] = useState<Dayjs>(() => dayjs().startOf('day'));
    const [mode, setMode] = useState<RangeMode>('week');
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});
    const [showRaw, setShowRaw] = useState(false);

    const isAdmin = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN;

    const range = useMemo(() => {
        if (mode === 'month') {
            return {from: anchor.startOf('month'), to: anchor.endOf('month').startOf('day')};
        }

        // Wochenbeginn bewusst selbst gerechnet: dayjs' startOf('week') richtet sich nach der
        // Locale und fängt in der Vorgabe-Locale sonntags an. Die Plantafel denkt in Werkwochen,
        // also Montag — und dafür extra das isoWeek-Plugin zu laden wäre unverhältnismäßig.
        const monday = anchor.subtract((anchor.day() + 6) % 7, 'day').startOf('day');
        return {from: monday, to: monday.add(6, 'day')};
    }, [anchor, mode]);

    useEffect(() => {
        setAccessLoading(true);
        context.api.calendar
            .calendarGetMyAccess()
            .then((response) => setAccess(response.data))
            .catch(context.apiError)
            .finally(() => setAccessLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(() => {
        if (!access?.available) return;

        setLoading(true);
        setError(undefined);
        context.api.calendar
            .calendarGetMyCalendar({
                von: range.from.format('YYYY-MM-DD'),
                bis: range.to.format('YYYY-MM-DD'),
            })
            .then((response) => setView(response.data))
            .catch((e) => {
                // Die Anbindung kann zwischen Access-Abfrage und Abruf wegbrechen (503) — dann
                // gehört die Begründung des Servers auf die Seite, nicht in eine Snackbar, die
                // wegläuft.
                const message = typeof e?.response?.data === 'string'
                    ? e.response.data
                    : e?.response?.data?.message ?? e?.message;
                setError(message ?? t('calendar.loadFailed'));
                setView(undefined);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access?.available, range.from, range.to]);

    useEffect(() => {
        load();
    }, [load]);

    /** Die Tage des Zeitfensters, auch die leeren — ein Kalender ohne Lücken liest sich falsch. */
    const days = useMemo(() => {
        const result: {key: string; date: Dayjs; entries: CalendarEntryDto[]}[] = [];
        const byDay = new Map<string, CalendarEntryDto[]>();

        for (const entry of view?.entries ?? []) {
            const key = entry.date
                ? dayjs(entry.date).format('YYYY-MM-DD')
                : entry.start
                    ? dayjs(entry.start).format('YYYY-MM-DD')
                    : 'ohne-datum';
            const list = byDay.get(key) ?? [];
            list.push(entry);
            byDay.set(key, list);
        }

        for (let cursor = range.from; !cursor.isAfter(range.to, 'day'); cursor = cursor.add(1, 'day')) {
            const key = cursor.format('YYYY-MM-DD');
            const entries = (byDay.get(key) ?? []).sort((a, b) => {
                if (a.isAllDay !== b.isAllDay) return a.isAllDay ? -1 : 1;
                return (a.start ?? '').localeCompare(b.start ?? '');
            });
            result.push({key, date: cursor, entries});
        }

        return result;
    }, [view?.entries, range.from, range.to]);

    const entryCount = view?.entries?.length ?? 0;
    const taskCount = view?.tasks?.length ?? 0;

    const dayLabel = (date: Dayjs) =>
        new Intl.DateTimeFormat(i18n.language, {weekday: 'short', day: '2-digit', month: '2-digit'})
            .format(date.toDate());

    const rangeLabel = mode === 'week'
        ? `${range.from.format('DD.MM.')} – ${range.to.format('DD.MM.YYYY')}`
        : new Intl.DateTimeFormat(i18n.language, {month: 'long', year: 'numeric'}).format(anchor.toDate());

    if (accessLoading) {
        return <Box sx={{display: 'flex', justifyContent: 'center', p: 6}}><CircularProgress/></Box>;
    }

    if (!access?.available) {
        return (
            <Box sx={{p: {xs: 2, md: 3}, maxWidth: 900}}>
                <Typography variant="h5" gutterBottom>{t('calendar.title')}</Typography>
                <Alert severity={access?.hasLabelUser === false ? 'info' : 'warning'} sx={{mt: 2}}>
                    <AlertTitle>{t('calendar.unavailableTitle')}</AlertTitle>
                    {access?.message}
                    {access?.hasLabelUser === false && isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Link component={RouterLink} to="/admin/users">
                                {t('calendar.goToUsers')}
                            </Link>
                        </Box>
                    )}
                    {access?.hasLabelUser === false && !isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Typography variant="body2">{t('calendar.askAdmin')}</Typography>
                        </Box>
                    )}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{p: {xs: 2, md: 3}}}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={2}
                   sx={{alignItems: {md: 'center'}, justifyContent: 'space-between', mb: 2}}>
                <Box>
                    <Typography variant="h5">{t('calendar.title')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('calendar.subtitle', {
                            name: access.employee?.name ?? '—',
                            employeeId: access.employee?.employeeId ?? '—',
                            labelUser: access.labelUser ?? '—',
                        })}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                    <ToggleButtonGroup size="small" exclusive value={mode}
                                       onChange={(_, value: RangeMode | null) => value && setMode(value)}>
                        <ToggleButton value="week">{t('calendar.modeWeek')}</ToggleButton>
                        <ToggleButton value="month">{t('calendar.modeMonth')}</ToggleButton>
                    </ToggleButtonGroup>

                    <Tooltip title={t('calendar.previous')}>
                        <IconButton size="small"
                                    onClick={() => setAnchor(anchor.subtract(1, mode === 'week' ? 'week' : 'month'))}>
                            <FontAwesomeIcon icon={faAngleLeft}/>
                        </IconButton>
                    </Tooltip>
                    <Button size="small" startIcon={<FontAwesomeIcon icon={faCalendarDay}/>}
                            onClick={() => setAnchor(dayjs().startOf('day'))}>
                        {t('calendar.today')}
                    </Button>
                    <Tooltip title={t('calendar.next')}>
                        <IconButton size="small"
                                    onClick={() => setAnchor(anchor.add(1, mode === 'week' ? 'week' : 'month'))}>
                            <FontAwesomeIcon icon={faAngleRight}/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('calendar.reload')}>
                        <IconButton size="small" onClick={load} disabled={loading}>
                            <FontAwesomeIcon icon={faArrowsRotate}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 2, flexWrap: 'wrap', rowGap: 1}}>
                <Typography variant="subtitle1" sx={{fontWeight: 500}}>{rangeLabel}</Typography>
                <Chip size="small" label={t('calendar.entryCount', {count: entryCount})}/>
                {taskCount > 0 && <Chip size="small" variant="outlined"
                                        label={t('calendar.taskCount', {count: taskCount})}/>}
                {!access.allowWrites && (
                    <Chip size="small" color="default" variant="outlined" label={t('calendar.readOnly')}/>
                )}
                {loading && <CircularProgress size={16}/>}
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            <Stack spacing={1.5}>
                {days.map(({key, date, entries}) => {
                    const isToday = date.isSame(dayjs(), 'day');
                    const isWeekend = date.day() === 0 || date.day() === 6;

                    return (
                        <Paper key={key} variant="outlined"
                               sx={{
                                   p: 1.5,
                                   borderColor: isToday ? 'primary.main' : undefined,
                                   bgcolor: isWeekend && entries.length === 0 ? 'action.hover' : undefined,
                               }}>
                            <Stack direction="row" spacing={1} sx={{alignItems: 'baseline', mb: entries.length ? 1 : 0}}>
                                <Typography variant="subtitle2"
                                            color={isToday ? 'primary' : 'text.primary'}
                                            sx={{minWidth: 110}}>
                                    {dayLabel(date)}
                                </Typography>
                                {entries.length === 0 && (
                                    <Typography variant="body2" color="text.disabled">
                                        {t('calendar.noEntries')}
                                    </Typography>
                                )}
                            </Stack>

                            <Stack spacing={1}>
                                {entries.map((entry) => (
                                    <EntryRow key={entry.id}
                                              entry={entry}
                                              expanded={!!expanded[entry.id ?? -1]}
                                              onToggle={() => setExpanded((prev) => ({
                                                  ...prev,
                                                  [entry.id ?? -1]: !prev[entry.id ?? -1],
                                              }))}/>
                                ))}
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>

            {/*
              * Wiedervorlagen aus der Tabelle "termine" — eine getrennte Quelle. Trotz des
              * Tabellennamens sind das Aufgaben mit Fälligkeitstag, nicht Einträge der Plantafel:
              * die meisten tragen keine Uhrzeit. Deshalb stehen sie als eigene Liste und werden
              * nicht in die Tage oben einsortiert.
              */}
            {taskCount > 0 && (
                <Box sx={{mt: 3}}>
                    <Divider sx={{mb: 2}}/>
                    <Typography variant="subtitle1" sx={{mb: 1}}>
                        {t('calendar.tasksTitle', {count: taskCount})}
                    </Typography>
                    <Stack spacing={0.75}>
                        {(view?.tasks ?? []).map((task) => (
                            <Paper key={task.id} variant="outlined" sx={{p: 1}}>
                                <Stack direction="row" spacing={1}
                                       sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5}}>
                                    <Typography variant="body2" sx={{fontFamily: 'monospace', minWidth: 92}}>
                                        {task.date ? dayjs(task.date).format('DD.MM.YYYY') : '—'}
                                    </Typography>
                                    {task.start && (
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{fontFamily: 'monospace'}}>
                                            {dayjs(task.start).format('HH:mm')}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{fontWeight: 500}}>
                                        {task.subject || '—'}
                                    </Typography>
                                    {task.activity && <Chip size="small" variant="outlined" label={task.activity}/>}
                                    {task.status && (
                                        <Chip size="small" variant="outlined"
                                              color={task.status.toLowerCase() === 'erledigt' ? 'success' : 'warning'}
                                              label={task.status}/>
                                    )}
                                    {!!task.priority && (
                                        <Chip size="small" variant="outlined"
                                              label={t('calendar.priority', {value: task.priority})}/>
                                    )}
                                    <Box sx={{flexGrow: 1}}/>
                                    {task.createdBy && (
                                        <Typography variant="caption" color="text.secondary">
                                            {task.createdBy}
                                        </Typography>
                                    )}
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Rohdaten der Antwort — für das Abnehmen der Anbindung gegen die Plantafel. */}
            {view && (
                <Box sx={{mt: 3}}>
                    <Divider sx={{mb: 2}}/>
                    <Button size="small" onClick={() => setShowRaw(!showRaw)}
                            startIcon={<FontAwesomeIcon icon={showRaw ? faChevronDown : faChevronRight}/>}>
                        {t('calendar.showRaw')}
                    </Button>
                    <Collapse in={showRaw}>
                        <Paper variant="outlined" sx={{mt: 1, p: 1.5, overflowX: 'auto'}}>
                            <Typography component="pre" variant="caption"
                                        sx={{m: 0, fontFamily: 'monospace', whiteSpace: 'pre'}}>
                                {JSON.stringify(view, null, 2)}
                            </Typography>
                        </Paper>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
};

/** Farbe der Kategorie-Marke. Aufträge und Abwesenheiten verwaltet Labelwin, nicht diese Seite. */
const kindColor = (kind?: string | null): 'primary' | 'success' | 'warning' | 'info' | 'default' => {
    switch (kind) {
        case 'Order':
            return 'primary';
        case 'Appointment':
            return 'success';
        case 'Absence':
            return 'warning';
        case 'Project':
            return 'info';
        default:
            return 'default';
    }
};

/**
 * Eine Zeile der Plantafel. Der aufklappbare Teil zeigt bewusst die Rohfelder: beim Abnehmen der
 * Anbindung will man `rawKind`, `headId` und `isPartOfSpan` sehen, nicht nur eine hübsche Zeile.
 */
const EntryRow: React.FC<{ entry: CalendarEntryDto; expanded: boolean; onToggle: () => void }> = (
    {entry, expanded, onToggle}) => {
    const {t} = useTranslation();

    const time = entry.isAllDay
        ? t('calendar.allDay')
        : entry.start && entry.end
            ? `${dayjs(entry.start).format('HH:mm')}–${dayjs(entry.end).format('HH:mm')}`
            : '—';

    return (
        <Box sx={{pl: 1, borderLeft: 3, borderColor: `${kindColor(entry.kind)}.main`}}>
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5}}>
                <Typography variant="body2" sx={{fontFamily: 'monospace', minWidth: 92}}>{time}</Typography>
                <Typography variant="body2" sx={{fontWeight: 500}}>{entry.subject || '—'}</Typography>
                <Chip size="small" color={kindColor(entry.kind)} variant="outlined"
                      label={entry.kind ?? t('calendar.unknownKind')}/>
                {entry.orderNumber && <Chip size="small" variant="outlined" label={entry.orderNumber}/>}
                {entry.project && <Chip size="small" variant="outlined" label={entry.project}/>}
                {typeof entry.hours === 'number' && (
                    <Typography variant="caption" color="text.secondary">
                        {t('calendar.hours', {hours: entry.hours})}
                    </Typography>
                )}
                {entry.isPartOfSpan && (
                    <Chip size="small" color="warning" variant="outlined" label={t('calendar.partOfSpan')}/>
                )}
                <Box sx={{flexGrow: 1}}/>
                <IconButton size="small" onClick={onToggle} aria-label={t('calendar.details')}>
                    <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} size="xs"/>
                </IconButton>
            </Stack>

            <Collapse in={expanded}>
                <Box sx={{pt: 0.5, pb: 1}}>
                    {entry.tooltip && (
                        <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-wrap', mb: 1}}>
                            {entry.tooltip}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace'}}>
                        id {entry.id} · headId {entry.headId} · montnr {entry.employeeId} · belegart {entry.rawKind}
                        {entry.orderId != null && ` · kdauftraglfdnr ${entry.orderId}`}
                        {entry.orderProject && ` · ${entry.orderProject}`}
                        {entry.spanStart && ` · Balken ${dayjs(entry.spanStart).format('DD.MM. HH:mm')}`}
                        {entry.spanEnd && `–${dayjs(entry.spanEnd).format('DD.MM. HH:mm')}`}
                    </Typography>
                </Box>
            </Collapse>
        </Box>
    );
};

export default MyCalendar;
